import { useEffect, useId, useRef, useState } from 'react'
import { ChevronDown } from 'lucide-react'
import { GUEST_LOCALES, type GuestLocale } from '@/lib/guestLocale'
import { useGuestLocale, useLandingCopy, useSetGuestLocale } from '@/hooks/useGuestLocale'
import { cn } from '@/components/ui/cn'

export function LandingLocaleSwitcher({
  className,
  ariaLabel,
}: {
  className?: string
  /** Override landing copy label (e.g. auth pages). */
  ariaLabel?: string
}) {
  const locale = useGuestLocale()
  const setLocale = useSetGuestLocale()
  const copy = useLandingCopy()
  const switcherLabel = ariaLabel ?? copy.localeSwitcherLabel
  const [open, setOpen] = useState(false)
  const rootRef = useRef<HTMLDivElement>(null)
  const listId = useId()
  const current = GUEST_LOCALES.find((item) => item.code === locale) ?? GUEST_LOCALES[0]

  useEffect(() => {
    if (!open) return
    const onPointerDown = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false)
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('mousedown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [open])

  const select = (next: GuestLocale) => {
    setLocale(next)
    setOpen(false)
  }

  return (
    <div ref={rootRef} className={cn('relative', className)} data-testid="landing-locale-switcher">
      <button
        type="button"
        className="inline-flex items-center gap-1.5 rounded-lg border border-border/70 bg-card/70 px-2.5 py-1.5 text-sm text-foreground transition-colors hover:bg-card"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listId}
        aria-label={switcherLabel}
        onClick={() => setOpen((value) => !value)}
      >
        <span aria-hidden className="text-base leading-none">
          {current.flag}
        </span>
        <span className="hidden sm:inline">{current.nativeLabel}</span>
        <ChevronDown className={cn('h-3.5 w-3.5 text-muted-foreground transition-transform', open && 'rotate-180')} />
      </button>

      {open ? (
        <ul
          id={listId}
          role="listbox"
          aria-label={switcherLabel}
          className="absolute right-0 z-40 mt-1.5 min-w-[11rem] overflow-hidden rounded-xl border border-border/70 bg-card py-1 shadow-lg"
        >
          {GUEST_LOCALES.map((item) => {
            const selected = item.code === locale
            return (
              <li key={item.code} role="option" aria-selected={selected}>
                <button
                  type="button"
                  className={cn(
                    'flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm transition-colors hover:bg-muted/60',
                    selected && 'bg-primary/10 text-foreground',
                  )}
                  onClick={() => select(item.code)}
                  data-testid={`landing-locale-${item.code}`}
                >
                  <span aria-hidden className="text-base leading-none">
                    {item.flag}
                  </span>
                  <span>{item.nativeLabel}</span>
                </button>
              </li>
            )
          })}
        </ul>
      ) : null}
    </div>
  )
}
